import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import '../input.css'
import Seo from './Seo'
import SiteAmbient from './common/SiteAmbient'
import SuccessStoriesSection from './HomePage/SuccessStoriesSection'
import useSiteRevampContent from '../content/useSiteRevampContent'

// Merged in from the old /evidence page, which now redirects here. One page
// answers "can I trust you" instead of two overlapping ones.
const metrics = {
  'en-US': [
    { value: 'US$9M+', label: 'annual operational savings documented at Vale' },
    { value: '80%',    label: 'support tickets reduction at Softplan, enterprise scale' },
    { value: '10+',    label: 'high-stakes transformation programs delivered' },
    { value: '30+',    label: 'years building software and operations' },
    { value: '3,000+', label: 'people trained in Lean, Agile and engineering practices' },
  ],
  'pt-BR': [
    { value: 'R$50M+', label: 'de economia operacional anual documentada na Vale' },
    { value: '80%',    label: 'de reducao de tickets de suporte na Softplan, escala enterprise' },
    { value: '10+',    label: 'programas de transformacao complexos entregues' },
    { value: '30+',    label: 'anos construindo software e operacoes' },
    { value: '3.000+', label: 'pessoas treinadas em Lean, Agile e praticas de engenharia' },
  ],
  'es': [
    { value: 'US$9M+', label: 'en ahorros operativos anuales documentados en Vale' },
    { value: '80%',    label: 'de reduccion de tickets de soporte en Softplan, escala enterprise' },
    { value: '10+',    label: 'programas de transformacion de alta complejidad entregados' },
    { value: '30+',    label: 'anos construyendo software y operaciones' },
    { value: '3.000+', label: 'personas formadas en Lean, Agile y practicas de ingenieria' },
  ],
}

const caseStudiesLead = {
  'en-US': { eyebrow: 'Track record', headline: 'What we did, and what it changed.' },
  'pt-BR': { eyebrow: 'Historico', headline: 'O que fizemos, e o que mudou.' },
  'es':    { eyebrow: 'Historial', headline: 'Lo que hicimos, y lo que cambio.' },
}

const normalizeLang = (lang) => {
  if (!lang) return 'en-US'
  const l = lang.toLowerCase()
  if (l.startsWith('pt')) return 'pt-BR'
  if (l.startsWith('es')) return 'es'
  return 'en-US'
}

function useReveal(threshold = 0.12) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return undefined
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

const AboutUsPage = () => {
  const content = useSiteRevampContent()
  const { i18n } = useTranslation()
  const lang = normalizeLang(i18n.language)
  const metricsRef = useReveal(0.08)
  const casesRef = useReveal(0.08)

  // Each section gets its own ref
  const headerRef   = useReveal(0.1)
  const cardsRef    = useReveal(0.1)
  const collageRef  = useReveal(0.08)
  const contextRef  = useReveal(0.08)
  const leaderRef   = useReveal(0.08)
  const bioRef      = useReveal(0.08)
  const closeRef    = useReveal(0.1)
  const ctaRef      = useReveal(0.1)

  const cardColors = [
    { bar: 'linear-gradient(90deg, #c9d33a, transparent)', border: 'rgba(201,211,58,0.15)' },
    { bar: 'linear-gradient(90deg, #f88116, transparent)', border: 'rgba(248,129,22,0.15)' },
    { bar: 'linear-gradient(90deg, #19a2cf, transparent)', border: 'rgba(25,162,207,0.15)' },
  ]

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white min-h-screen">
      <SiteAmbient />
      <Seo
        title={content.seo.story.title}
        description={content.seo.story.description}
        path="/about"
        image="/images/collage.png"
      />

      {/* ── Hero header ──────────────────────────────────────────────── */}
      <section className="relative overflow-hidden px-6 pt-32 pb-24 md:px-16">
        <div ref={headerRef} className="relative z-10 mx-auto max-w-5xl reveal-on-scroll">
          <p className="text-xs uppercase tracking-[0.44em] text-pyramid-1/75">
            {content.story.eyebrow}
          </p>
          <h1 className="mt-4 max-w-3xl text-[clamp(2.2rem,4.5vw,4rem)] font-light leading-tight text-white">
            {content.story.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
            {content.story.intro}
          </p>
        </div>
      </section>

      {/* ── Proof numbers, merged from the old evidence page ─────────── */}
      <section className="px-6 pb-4 md:px-16">
        <div className="mx-auto max-w-6xl">
          <div ref={metricsRef} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5 stagger-grid">
            {metrics[lang].map((item, i) => (
              <div
                key={item.value}
                className="stat-card stagger-card rounded-2xl p-7"
                style={{ '--card-delay': `${i * 100}ms` }}
              >
                <p className="text-4xl font-light tracking-tight text-pyramid-1">{item.value}</p>
                <div className="mt-3 h-px w-8 bg-pyramid-1/30" />
                <p className="mt-3 text-sm leading-6 text-slate-400">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Story cards ──────────────────────────────────────────────── */}
      <section className="px-6 py-20 md:px-16">
        <div className="mx-auto max-w-6xl">
          <div ref={cardsRef} className="grid gap-6 md:grid-cols-3 stagger-grid">
            {content.story.cards.map((card, i) => (
              <div
                key={card.title}
                className="stagger-card rounded-2xl p-7 card-hover transition-all"
                style={{
                  '--card-delay': `${i * 130}ms`,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                  border: `1px solid ${cardColors[i].border}`,
                }}
              >
                <div
                  className="mb-4 h-1 w-12 rounded-full"
                  style={{ background: cardColors[i].bar }}
                />
                <h3 className="text-xl font-semibold text-white">{card.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-300">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Collage + context ───────────────────────────────────────── */}
      <section className="px-6 py-20 md:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 lg:grid-cols-2 items-start">

            {/* Collage image */}
            <div ref={collageRef} className="reveal-on-scroll">
              <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
                <img
                  src="/images/collage.png"
                  alt="Lean it 101 history"
                  className="w-full h-auto object-cover transition-transform duration-700 hover:scale-[1.02]"
                />
              </div>
            </div>

            {/* Story context */}
            <div ref={contextRef} className="reveal-on-scroll flex flex-col gap-6" style={{ '--reveal-delay': '100ms' }}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                <p className="text-xs uppercase tracking-[0.35em] text-pyramid-1/80 mb-3">
                  {content.story.evidenceTitle}
                </p>
                <p className="leading-relaxed text-slate-300">
                  {content.story.evidenceIntro}
                </p>
              </div>

              {/* Track record numbers */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {[
                  { value: 'R$50M', sub: 'Vale' },
                  { value: '80%', sub: 'Softplan' },
                  { value: '10+', sub: 'years' },
                ].map((stat) => (
                  <div
                    key={stat.value}
                    className="stat-card rounded-xl px-2 py-4 sm:p-5 text-center"
                  >
                    <p className="text-xl sm:text-2xl font-light text-pyramid-1">{stat.value}</p>
                    <p className="mt-1 text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide sm:tracking-widest whitespace-nowrap">{stat.sub}</p>
                  </div>
                ))}
              </div>

              <a
                href="#evidence"
                className="inline-flex items-center gap-2 text-sm text-pyramid-1/80 hover:text-pyramid-1 transition-colors group"
              >
                See full case studies
                <span className="transition-transform group-hover:translate-x-1" aria-hidden="true">&#8594;</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Case studies, merged from the old evidence page ──────────── */}
      <section className="px-4 py-16 md:px-10">
        <div className="mx-auto max-w-6xl">
          <div ref={casesRef} className="reveal-on-scroll px-2 md:px-6">
            <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/75">
              {caseStudiesLead[lang].eyebrow}
            </p>
            <h2 className="mt-4 max-w-3xl text-3xl font-light leading-tight text-white md:text-4xl">
              {caseStudiesLead[lang].headline}
            </h2>
          </div>
          <div className="mt-10">
            <div id="evidence" className="scroll-mt-24"><SuccessStoriesSection /></div>
          </div>
        </div>
      </section>

      {/* ── How the work is staffed ─────────────────────────────────── */}
      <section className="relative px-6 py-20 md:px-16 overflow-hidden">
        <div
          className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(25,162,207,0.6) 0%, transparent 70%)',
            filter: 'blur(60px)',
            transform: 'translate(20%, -20%)',
          }}
        />

        <div className="relative z-10 mx-auto max-w-6xl">
          <div className="grid gap-6 lg:grid-cols-2 items-start">

            {/* How the work is staffed: the capacity question, answered first */}
            <div ref={leaderRef} className="reveal-on-scroll rounded-2xl border border-pyramid-19/25 bg-white/[0.03] p-8 card-hover">
              <p className="text-xs uppercase tracking-[0.3em] text-pyramid-1/70">
                {content.story.leadershipRole}
              </p>
              <h2 className="mt-4 text-2xl font-semibold text-white">
                {content.story.leadershipTitle}
              </h2>
              <div className="mt-4 space-y-4">
                {content.story.leadershipBody.map((paragraph) => (
                  <p key={paragraph} className="leading-relaxed text-slate-300">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Where the depth comes from */}
            <div className="flex flex-col gap-6">
              <div ref={bioRef} className="reveal-on-scroll rounded-2xl border border-white/10 bg-white/[0.03] p-8 card-hover" style={{ '--reveal-delay': '80ms' }}>
                <h2 className="text-2xl font-semibold text-white">
                  {content.story.practiceTitle}
                </h2>
                <div className="mt-4 space-y-4">
                  {content.story.practiceBody.map((paragraph) => (
                    <p key={paragraph} className="leading-relaxed text-slate-300">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              <div ref={closeRef} className="reveal-on-scroll rounded-2xl border border-white/10 bg-white/[0.03] p-8 card-hover" style={{ '--reveal-delay': '160ms' }}>
                <h3 className="text-2xl font-semibold text-white">
                  {content.story.closeTitle}
                </h3>
                <p className="mt-4 text-lg leading-relaxed text-slate-300">
                  {content.story.closeBody}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ───────────────────────────────────────────────── */}
      <section className="px-6 py-20 md:px-16">
        <div ref={ctaRef} className="mx-auto max-w-4xl reveal-on-scroll">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] via-white/[0.025] to-transparent p-10 text-center">
            <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/70">Benjamin</p>
            <h2 className="mt-4 text-2xl font-semibold text-white md:text-3xl">
              {content.story.closeTitle}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://benjaminintelligence.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-6 py-3 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5"
              >
                Explore Benjamin
                <span aria-hidden="true">&#8599;</span>
              </a>
              <a
                href="#evidence"
                className="inline-flex items-center justify-center rounded-[10px] border border-white/20 bg-white/5 px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 hover:border-white/40"
              >
                Our work
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AboutUsPage
